import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { TranslatePipe } from '@kirigami/shared/i18n';
import { KirigamiButton } from '@kirigami/shared/ui';

/**
 * Contact call-to-action, per the landing refinement design: brand-blue band
 * with the invitation and a light pill button, each rising into place as the
 * section enters the viewport (same reveal behaviour as the why section).
 * The button keeps emitting `contact`; the page decides where it goes.
 */
@Component({
  selector: 'app-contact-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KirigamiButton, TranslatePipe, UpperCasePipe],
  template: `
    <section id="contact" aria-labelledby="contact-title" class="contact">
      <div class="contact__inner">
        <h2 id="contact-title" class="contact__title" data-reveal>
          {{ 'landing.contact.title' | t }}
        </h2>
        <p class="contact__text" data-reveal style="--d: 0.1s">
          {{ 'landing.contact.subtitle' | t }}
        </p>
        <div class="contact__actions" data-reveal style="--d: 0.2s">
          <kirigami-button
            color="secondary"
            [filled]="true"
            [label]="'landing.contact.cta' | t | uppercase"
            [ariaLabel]="'landing.contact.cta' | t"
            styleClass="contact-cta"
            (onClick)="contact.emit()"
          />
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      /* kirigami-600 (not the design's 500) so the 20px subtitle meets the
         4.5:1 AA contrast minimum against the band. */
      .contact {
        background: var(--kirigami-600);
        padding: 9.0625rem 1.5rem 9.6875rem;
      }
      @media (min-width: 1024px) {
        .contact {
          padding: 9.0625rem 4.0625rem 9.6875rem;
        }
      }
      .contact__inner {
        max-width: 63.125rem;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 1.125rem;
      }
      .contact__title {
        margin: 0;
        font-size: clamp(2rem, 4vw, 2.5rem);
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: -0.011em;
        color: var(--neu-0);
      }
      .contact__text {
        margin: 0;
        font-size: 1.25rem;
        line-height: 1.5;
        color: var(--kirigami-50);
        text-wrap: pretty;
      }
      .contact__actions {
        margin-top: 2.5rem;
      }

      /* Design CTA: light pill on the blue band, brightening to pure white.
         .k-btn is doubled so the override outranks the button's own
         color-variant rules regardless of stylesheet insertion order. */
      :host ::ng-deep .k-btn.k-btn.contact-cta {
        height: 3.625rem;
        padding: 0 2.5rem;
        border: 0;
        border-radius: 999px;
        background: var(--kirigami-50);
        /* kirigami-600 label so the 15px text meets the 4.5:1 AA minimum. */
        color: var(--kirigami-600);
        transition:
          background-color 0.18s ease,
          color 0.18s ease;
      }
      :host ::ng-deep .k-btn.k-btn.contact-cta:hover {
        background: var(--neu-0);
        color: var(--kirigami-700);
      }
      /* The CTA override outranks the global focus ring, so the ring is
         restated here at the same specificity. kirigami-700 outer ring:
         600 would vanish against the 600 band. */
      :host ::ng-deep .k-btn.k-btn.contact-cta:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 1px var(--neu-0),
          0 0 0 2px var(--neu-0),
          0 0 0 4px var(--kirigami-700);
      }
      :host ::ng-deep .k-btn.k-btn.contact-cta .k-label {
        font-size: 0.9375rem;
        font-weight: 700;
        letter-spacing: 0.06em;
      }

      .contact--reveal-armed [data-reveal] {
        opacity: 0;
      }
      .contact--reveal-armed [data-reveal].is-in {
        animation: contact-rise 0.82s cubic-bezier(0.16, 1, 0.3, 1) var(--d, 0s) both;
      }
      @keyframes contact-rise {
        from {
          opacity: 0;
          transform: translateY(46px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
  ],
})
export class ContactWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private observer?: IntersectionObserver;

  @Output() readonly contact = new EventEmitter<void>();
  @Output() readonly bookDemo = new EventEmitter<void>();

  ngAfterViewInit(): void {
    if (
      typeof matchMedia === 'undefined' ||
      typeof IntersectionObserver === 'undefined' ||
      matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const section = this.host.nativeElement.querySelector<HTMLElement>('.contact');
    if (!section) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 0.18) entry.target.classList.add('is-in');
          else if (entry.intersectionRatio === 0) entry.target.classList.remove('is-in');
        }
      },
      { threshold: [0, 0.18, 0.5] },
    );

    section.classList.add('contact--reveal-armed');
    section.querySelectorAll('[data-reveal]').forEach((el) => this.observer?.observe(el));
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
